import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, parseISO, startOfWeek } from 'date-fns';
import { tenantToday } from '@utils/timeZone';
import { longDay, shortDay } from '@utils/i18n';
import { cn } from '@utils/cn';
import { chipClass } from '../constants';
import { MonthGrid } from '../monthGrid';
import { PhoneSection } from '../section';
import { WEEKDAY_ORDER } from './constants';
import { dayKey, getIncludedDays, inRange, isIncluded, tapDay } from './helpers';
import { PhoneMultiDayProps } from './types';

export const PhoneMultiDay = ({ value, onChange, dots, disabled }: PhoneMultiDayProps) => {
  const { t } = useTranslation('app');
  const [month, setMonth] = useState(() => (value.start ? parseISO(value.start) : tenantToday()));
  const count = getIncludedDays(value).length;
  const today = dayKey(tenantToday());
  const monday = startOfWeek(tenantToday(), { weekStartsOn: 1 });

  return (
    <PhoneSection
      title={t('scheduling.phone.dates')}
      badge={
        <span
          className={cn(
            'flex h-6 items-center whitespace-nowrap rounded-full px-2.5 text-xs font-bold',
            count ? 'bg-primary-50 text-primary-600' : 'bg-neutral-100 text-neutral-500',
          )}>
          {t('scheduling.phone.days', { count })}
        </span>
      }
      right={
        <button
          type="button"
          disabled={disabled || !value.start}
          onClick={() => onChange({ ...value, start: undefined, end: undefined, overrides: {} })}
          className="h-8 px-1 text-xs font-bold tracking-wide text-primary-600 disabled:opacity-40">
          {t('scheduling.phone.clear')}
        </button>
      }>
      <p className="-mt-1 text-xs text-neutral-500">
        {t(value.start && !value.end ? 'scheduling.phone.pickLast' : 'scheduling.phone.datesHint')}
      </p>
      <div className="rounded-xl border border-neutral-200 bg-white px-2.5 pb-2 pt-1.5">
        <MonthGrid
          month={month}
          onMonthChange={setMonth}
          onTap={(key) => !disabled && onChange(tapDay(value, key))}
          getDay={(key) => {
            const included = isIncluded(value, key);
            const skipped = !included && inRange(value, key);
            return {
              selected: included,
              muted: skipped,
              struck: skipped,
              today: key === today,
              dot: included && dots.has(key),
              disabled,
              label: included
                ? t('scheduling.phone.included')
                : skipped
                  ? t('scheduling.phone.skipped')
                  : undefined,
            };
          }}
        />
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {WEEKDAY_ORDER.map((weekday) => {
          const on = value.weekdays.includes(weekday);
          const date = addDays(monday, (weekday + 6) % 7);
          return (
            <button
              key={weekday}
              type="button"
              disabled={disabled}
              aria-pressed={on}
              aria-label={longDay(date)}
              onClick={() =>
                onChange({
                  ...value,
                  weekdays: on
                    ? value.weekdays.filter((el) => el !== weekday)
                    : [...value.weekdays, weekday],
                })
              }
              className={cn('h-9 rounded-lg border text-[13px]', chipClass(on))}>
              {shortDay(date).slice(0, 2)}
            </button>
          );
        })}
      </div>
    </PhoneSection>
  );
};
